import capitalize from 'lodash/capitalize';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';

import { SiteStatus200 } from '@newfold/huapi-js/src/index.schemas';

import { useBrandInfo } from '~/hooks/useBrandInfo';

import SiteSsoLink from './SiteSsoLink';

export type WPPendingDialogPropOptions = {
  siteId?: string | number;
  siteStatus?: SiteStatus200;
  title?: string;
  ssoQueryParams?: string;
  open: boolean;
  onAction: VoidFunction;
  onClose: VoidFunction;
};

const WPPendingDialog = ({
  siteId = undefined,
  siteStatus = undefined,
  title = undefined,
  ssoQueryParams = '',
  onAction,
  onClose,
  open,
}: WPPendingDialogPropOptions) => {
  const { brandFromJWT: brand } = useBrandInfo();

  const hasContent =
    siteStatus?.pending_url || siteStatus?.cloudflare_ready === false;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted
      aria-describedby="interstitial-site-sso-dialog"
      maxWidth={hasContent ? 'md' : 'sm'}
      fullWidth
      data-testid={'wp-pending-dialog'}
    >
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="h2">
            {title || `We're still setting up a few things...`}
          </Typography>

          <Stepper orientation="vertical">
            {/* 1. First step always shows */}
            <Step active completed data-testid={'wp-pending-step-completed'}>
              <StepLabel>WordPress is installed</StepLabel>
            </Step>

            {/* 2. site url issue */}
            {siteStatus?.pending_url && (
              <Step active data-testid={'wp-pending-step-pendingurl'}>
                <StepLabel>Site URL</StepLabel>
                <StepContent>
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    This site is using a temporary URL until{' '}
                    <b>{siteStatus?.pending_url}</b> is pointed at{' '}
                    {capitalize(brand)}.
                  </Typography>
                  <Stack spacing={2} alignItems="start">
                    <Typography>
                      If this is a new domain name, it might take a few hours to
                      finish setting up.
                    </Typography>
                    {/* TODO: Need to straighten out the pending_url/external domain logic before showing them this button */}
                    {/* <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(`/sites/${siteId}/setup/settings`)
                    }
                  >
                    Finish Domain Setup
                  </Button> */}
                  </Stack>
                </StepContent>
              </Step>
            )}

            {/* 3. if pending cloudflare */}
            {siteStatus?.cloudflare_ready === false && (
              <Step active data-testid={'wp-pending-step-cloudflare'}>
                <StepLabel>Cloudflare</StepLabel>
                <StepContent>
                  <Typography>
                    We are also adding Cloudflare to your site. You can start
                    using WordPress right away.
                  </Typography>
                  <Typography>
                    If you are logged out during your session, return to this
                    tab to log in again.
                  </Typography>
                </StepContent>
              </Step>
            )}
          </Stepper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <SiteSsoLink
          variant="contained"
          ssoQueryParams={ssoQueryParams}
          onAction={onAction}
          id={siteId}
        >
          Log Into WordPress
        </SiteSsoLink>
      </DialogActions>
    </Dialog>
  );
};
export default WPPendingDialog;
